import { createContext, useCallback, useContext, useMemo, useState } from "react";

/** The page guide for the current route, as exposed to the top-bar help menu. */
export interface RegisteredPageGuide {
  title: string;
  /** True until the user has opened the current version of this guide. */
  isUnread: boolean;
  open: () => void;
}

interface PageGuideContextValue {
  activePageGuide: RegisteredPageGuide | null;
  registerPageGuide: (guide: RegisteredPageGuide) => () => void;
}

const PageGuideContext = createContext<PageGuideContextValue | null>(null);

export function PageGuideProvider({ children }: { children: React.ReactNode }) {
  const [activePageGuide, setActivePageGuide] = useState<RegisteredPageGuide | null>(null);

  const registerPageGuide = useCallback((guide: RegisteredPageGuide) => {
    setActivePageGuide(guide);
    return () => {
      // Only clear when this guide is still the active one, so a guide that
      // re-registers (or a newly mounted page) is not wiped by a stale cleanup.
      setActivePageGuide((current) => (current === guide ? null : current));
    };
  }, []);

  const value = useMemo(
    () => ({ activePageGuide, registerPageGuide }),
    [activePageGuide, registerPageGuide],
  );

  return <PageGuideContext.Provider value={value}>{children}</PageGuideContext.Provider>;
}

export function usePageGuide(): PageGuideContextValue {
  const ctx = useContext(PageGuideContext);
  if (!ctx) {
    throw new Error("usePageGuide must be used within PageGuideProvider");
  }
  return ctx;
}
